import copy from "copy-to-clipboard";
import {
  format, parseISO, differenceInSeconds, differenceInCalendarDays, differenceInCalendarYears,
} from "date-fns";
import Delta from "quill-delta";
import message from "./message";
import type { NebulaSiteEidtorBlock, NebulaSiteEidtorJson, NebulaSiteEidtorState } from "../type";
import type { App } from "vue";

export class Utils {
  // 手机号掩码：176****1234
  public formatMaskMobile(mobile: string): string {
    if (!mobile || mobile.length < 7) return mobile;
    return `${mobile.slice(0, 3)}****${mobile.slice(-4)}`;
  }

  // 日期格式化：2022-05-22 10:56:00
  public formatDateTime(args: { date?: Date | number; format?: string; default?: string }): string {
    const dateValue = args.date ?? new Date();
    const formatString = args.format ?? "yyyy-MM-dd HH:mm:ss";
    const defaultString = args.default ?? "";

    if (!args.date) return defaultString;
    if (typeof dateValue === "string") {
      return format(parseISO(dateValue), formatString);
    }
    if (dateValue instanceof Date) {
      return format(dateValue, formatString);
    }
    if (typeof dateValue === "number") {
      if (String(dateValue).length === 10) {
        return format(dateValue * 1000, formatString);
      }
      return format(dateValue, formatString);
    }
    return format(dateValue, formatString);
  }

  // 将编辑器 Delta 数据转换为富文本行数据
  public parseEditorJson(
    content: string | Record<string, unknown> | Array<unknown>,
  ): NebulaSiteEidtorJson {
    const state: NebulaSiteEidtorState = { content: {}, json: { version: 5, content: [] } };

    if (Array.isArray(content)) {
      state.json = { version: 5, content } as NebulaSiteEidtorJson;
      return state.json;
    }

    if (typeof content === "string") {
      try {
        state.content = JSON.parse(content);
      } catch {
        state.content = {};
      }
    } else {
      state.content = content || {};
    }

    if (state.content.version === 5 && Array.isArray(state.content.content)) {
      state.json.content = state.content.content;
    }

    if (state.content.version === "v1" && Array.isArray(state.content.ops)) {
      new Delta(state.content.ops).eachLine((line, attrs, index) => {
        const data: Array<NebulaSiteEidtorBlock> = [];
        for (const block of line.ops) {
          if (typeof block.insert === "string") {
            if (block.attributes?.link) {
              data.push({
                type: "link",
                text: block.insert,
                href: block.attributes?.link as string,
                attrs: block.attributes ?? {},
              });
            } else {
              data.push({ type: "text", text: block.insert, attrs: block.attributes ?? {} });
            }
            continue;
          }
          if (typeof block.insert?.image === "string") {
            data.push({ type: "image", src: block.insert.image, attrs: { ...block.attributes } });
            continue;
          }
        }
        state.json.content.push({ index, attrs: { ...attrs }, data });
      });
    }

    return state.json;
  }

  // 相对时间：刚刚、分钟前、小时前，超过三天显示日期
  public formatDateDistance(args: { date: Date | number | string; default?: string }): string {
    if (!args.date) return args.default ?? "";
    const state = { source: Date.now(), target: args.date };
    if (typeof state.target === "number") {
      if (String(state.target).length === 10) {
        state.target *= 1000;
      }
    }
    if (typeof state.target === "string") {
      state.target = parseISO(state.target);
    }

    const diffSeconds = differenceInSeconds(state.source, state.target);
    if (diffSeconds < 60) return "刚刚";
    if (diffSeconds < 60 * 60) return `${Math.floor(diffSeconds / 60)}分钟前`;
    if (diffSeconds < 10 * 60 * 60) return `${Math.floor(diffSeconds / (60 * 60))}小时前`;

    const diffDays = differenceInCalendarDays(state.source, state.target);
    if (diffDays === 0) return format(state.target, "HH:mm");
    if (diffDays === 1) return "昨天";
    if (diffDays === 2) return "2天前";
    if (diffDays === 3) return "3天前";

    const diffYears = differenceInCalendarYears(state.source, state.target);
    if (diffYears === 0) return format(state.target, "MM-dd");
    return format(state.target, "yyyy-MM-dd");
  }

  public async copyToClipboard(text: string): Promise<void> {
    const success = await copy(text);
    if (success) {
      message.info({ content: "复制成功", duration: 3000 });
    } else {
      message.info({ content: "复制失败", duration: 3000 });
    }
  }

  public install(app: App): void {
    Object.defineProperty(app.config.globalProperties, "$utils", {
      enumerable: true,
      get: () => this,
    });
  }
}

export default new Utils();
